// Utility to safely handle WaveSurfer loading and cleanup
export const createSafeWaveSurfer = async (container, options = {}) => {
    try {
      const WaveSurfer = (await import('wavesurfer.js')).default;
      const Regions = (await import('wavesurfer.js/dist/plugins/regions.esm.js')).default;
      const Timeline = (await import('wavesurfer.js/dist/plugins/timeline.esm.js')).default;
      
      // Initialize plugins
      const regionsPlugin = Regions.create({
        dragSelection: {
          slop: 5
        }
      });
      
      const timelinePlugin = Timeline.create({
        height: 20,
        insertPosition: 'afterend',
        timeInterval: 0.2,
        primaryLabelInterval: 5,
        secondaryLabelInterval: 1,
        style: {
          fontSize: '10px',
          color: '#2D3748',
        }
      });

      const defaultOptions = {
        container,
        waveColor: '#4F46E5',
        progressColor: '#7C3AED',
        cursorColor: '#EF4444',
        height: 120,
        normalize: true,
        backend: 'MediaElement', // Use MediaElement instead of WebAudio to avoid AbortController issues
        mediaControls: false,
        plugins: [regionsPlugin, timelinePlugin],
        ...options
      };
  
      const wavesurfer = WaveSurfer.create(defaultOptions);
      
      // Add safety wrapper for destroy method
      const originalDestroy = wavesurfer.destroy.bind(wavesurfer);
      wavesurfer.destroy = () => {
        try {
          // Cancel any pending operations first
          if (wavesurfer.isPlaying()) {
            wavesurfer.pause();
          }
          
          // Clear any media sources
          if (wavesurfer.getMediaElement) {
            const media = wavesurfer.getMediaElement();
            if (media) {
              media.pause();
              media.src = '';
              media.load();
            }
          }
          
          // Use a longer timeout to ensure all operations complete
          setTimeout(() => {
            try {
              originalDestroy();
            } catch (error) {
              // Specifically handle AbortError
              if (error.name === 'AbortError' || error.message?.includes('aborted')) {
                console.warn('WaveSurfer AbortError during destroy (safely ignored):', error.message);
              } else {
                console.warn('WaveSurfer destroy error (safely ignored):', error);
              }
            }
          }, 200); // Increased timeout
        } catch (error) {
          // Handle immediate errors
          if (error.name === 'AbortError' || error.message?.includes('aborted')) {
            console.warn('WaveSurfer AbortError during cleanup (safely ignored):', error.message);
          } else {
            console.warn('WaveSurfer cleanup error (safely ignored):', error);
          }
        }
      };
  
      // Return both wavesurfer and plugins for external access
      return {
        wavesurfer,
        regionsPlugin,
        timelinePlugin
      };
    } catch (error) {
      console.error('Error creating WaveSurfer:', error);
      return null;
    }
  };